// Baked light volume of a map (docs/ARCHITECTURE.md "Baked light"): decoding of `MapFile.bake`, the grids it
// holds and a CPU sampler with the same filtering and maths as the surface shader.
// No three.js here, so everything is unit-testable in node.

import type { MapBake, MapVec3 } from "./mapTypes";

/** The sky / bounce / tint grids have this many times the cell size of the occlusion grid. */
export const COARSE = 2;
/** Chroma bytes cover 0..CHROMA_MAX. */
export const CHROMA_MAX = 2;
/** The direction terms of the sky grid are stored divided by this. */
export const SKY_B_RANGE = 2;
/** Channels of the occlusion grid and of each coarse grid; the coarse planes follow in sky, bounce, tint order. */
const OCC_PLANES = 4;
const COARSE_PLANES = 12;
/** Format version understood by this decoder. */
export const BAKE_VERSION = 1;

export interface BakeLayout {
  /** Probes of the occlusion grid / of each coarse grid. */
  fine: number;
  coarse: number;
  /** Decoded byte length. */
  bytes: number;
}

export function bakeLayout(dims: MapVec3): BakeLayout {
  const fine = dims[0] * dims[1] * dims[2];
  const coarse = fine / (COARSE * COARSE * COARSE);
  return { fine, coarse, bytes: fine * OCC_PLANES + coarse * COARSE_PLANES };
}

/** RGBA8 voxel data of the four 3D textures (x fastest, then y, then z). */
export interface BakeGrids {
  /** Occlusion grid: openness for a normal n is `a + b . n` with a = R and b = GBA - 0.5. */
  occ: Uint8Array;
  /** Coarse: sky visibility relative to an open field, `a + b . n` with a = R and b = (GBA - 0.5) * SKY_B_RANGE. */
  sky: Uint8Array;
  /** Coarse: bounce luminance in the occlusion grid's `a + b . n` form (times `bounceScale`, relative to the sun). */
  bounce: Uint8Array;
  /** Coarse: R and G bounce chroma (r / Y, b / Y, times CHROMA_MAX), B sqrt-coded lamp luminance, A lamp colour position between the tints. */
  tint: Uint8Array;
}

/** A decoded bake: placement, scales and grids. */
export interface BakeVolume extends BakeGrids {
  origin: MapVec3;
  cell: number;
  /** Occlusion-grid dimensions. */
  dims: MapVec3;
  /** Dimensions of the coarse grids. */
  coarseDims: MapVec3;
  bounceScale: number;
  lampScale: number;
  lampTints: [MapVec3, MapVec3];
}

function checkHeader(b: MapBake): void {
  if (b.version !== BAKE_VERSION) throw new Error(`bake: unsupported version ${String(b.version)}`);
  if (!(b.cell > 0) || !Number.isFinite(b.cell)) throw new Error("bake: bad cell size");
  for (const n of b.dims) {
    if (!Number.isInteger(n) || n < COARSE || n % COARSE !== 0) throw new Error(`bake: bad dims ${b.dims.join("x")}`);
  }
}

/**
 * Interleaves four consecutive planes of `count` bytes starting at `offset` into RGBA texels, one 32-bit
 * write per texel (browsers run little-endian, where byte 0 is the lowest).
 */
function interleave(raw: Uint8Array, offset: number, count: number): Uint8Array {
  const out = new Uint8Array(count * 4);
  const texels = new Uint32Array(out.buffer);
  const r = raw.subarray(offset, offset + count);
  const g = raw.subarray(offset + count, offset + 2 * count);
  const b = raw.subarray(offset + 2 * count, offset + 3 * count);
  const a = raw.subarray(offset + 3 * count, offset + 4 * count);
  for (let p = 0; p < count; p++) texels[p] = r[p]! | (g[p]! << 8) | (b[p]! << 16) | (a[p]! << 24);
  return out;
}

/** Splits the decoded plane bytes into the four interleaved RGBA grids. */
export function unpackPlanes(raw: Uint8Array, dims: MapVec3): BakeGrids {
  const layout = bakeLayout(dims);
  if (raw.length !== layout.bytes) throw new Error(`bake: ${raw.length} bytes, expected ${layout.bytes}`);
  const { fine, coarse } = layout;
  const base = fine * OCC_PLANES;
  return {
    occ: interleave(raw, 0, fine),
    sky: interleave(raw, base, coarse),
    bounce: interleave(raw, base + 4 * coarse, coarse),
    tint: interleave(raw, base + 8 * coarse, coarse),
  };
}

/** Bytes of a base64 zlib stream, inflated off the main thread (the data URL is decoded by the browser, not by script). */
export async function inflateBase64(b64: string): Promise<Uint8Array> {
  const res = await fetch(`data:application/octet-stream;base64,${b64}`);
  const stream = res.body!.pipeThrough(new DecompressionStream("deflate"));
  return new Uint8Array(await new Response(stream).arrayBuffer());
}

/** Decodes `MapFile.bake`; throws on anything malformed. */
export async function decodeBake(bake: MapBake): Promise<BakeVolume> {
  checkHeader(bake);
  const grids = unpackPlanes(await inflateBase64(bake.data), bake.dims);
  return {
    ...grids,
    origin: bake.origin,
    cell: bake.cell,
    dims: bake.dims,
    coarseDims: [bake.dims[0] / COARSE, bake.dims[1] / COARSE, bake.dims[2] / COARSE],
    bounceScale: bake.bounceScale,
    lampScale: bake.lampScale,
    lampTints: bake.lampTints,
  };
}

/** Linear colour with the given chroma ratios (r / Y, b / Y) and unit luminance (green fills the rest). */
export function chromaColor(cr: number, cb: number, out: [number, number, number]): void {
  out[0] = cr;
  out[1] = Math.max(1 - 0.2126 * cr - 0.0722 * cb, 0) / 0.7152;
  out[2] = cb;
}

/** What the surface shader reads at a point, for a surface normal. */
export interface BakeSample {
  /** Local occlusion in 0..1 (1 = open). */
  ao: number;
  /** Mean openness over all six axes: higher than a flat surface's on convex edges. */
  openness: number;
  /** Sky visibility relative to an open field, in 0..1. */
  sky: number;
  /** Bounce light colour relative to the sun's irradiance (multiply by sunColor * sunIntensity). */
  bounce: [number, number, number];
  /** Lamp irradiance in the units of the map's point lights. */
  lamp: [number, number, number];
}

export type Texel = [number, number, number, number];

/** Trilinear fetch of an RGBA8 grid at unit coordinates, clamp-to-edge, texel centres at (i + 0.5) / n (GL_LINEAR). */
export function fetchLinear(data: Uint8Array, dims: MapVec3, u: number, v: number, w: number, out: Texel): void {
  const [nx, ny, nz] = dims;
  const fx = Math.min(Math.max(u * nx - 0.5, 0), nx - 1);
  const fy = Math.min(Math.max(v * ny - 0.5, 0), ny - 1);
  const fz = Math.min(Math.max(w * nz - 0.5, 0), nz - 1);
  const x0 = Math.floor(fx);
  const y0 = Math.floor(fy);
  const z0 = Math.floor(fz);
  const x1 = Math.min(x0 + 1, nx - 1);
  const y1 = Math.min(y0 + 1, ny - 1);
  const z1 = Math.min(z0 + 1, nz - 1);
  const tx = fx - x0;
  const ty = fy - y0;
  const tz = fz - z0;
  out[0] = out[1] = out[2] = out[3] = 0;
  for (let k = 0; k < 2; k++) {
    for (let j = 0; j < 2; j++) {
      for (let i = 0; i < 2; i++) {
        const wgt = (i === 0 ? 1 - tx : tx) * (j === 0 ? 1 - ty : ty) * (k === 0 ? 1 - tz : tz);
        const idx = ((k === 0 ? z0 : z1) * ny * nx + (j === 0 ? y0 : y1) * nx + (i === 0 ? x0 : x1)) * 4;
        for (let c = 0; c < 4; c++) out[c] = out[c]! + data[idx + c]! * wgt;
      }
    }
  }
  for (let c = 0; c < 4; c++) out[c] = out[c]! / 255;
}

const clamp01 = (v: number): number => Math.min(1, Math.max(0, v));
const texel: Texel = [0, 0, 0, 0];
const chroma: [number, number, number] = [0, 0, 0];

/**
 * Samples the bake as the surface shader does: at `p + n * offset` (metres), for the unit normal n.
 * Used for objects that are not map surfaces (the shader's own maths lives in surfaceShader.ts).
 */
export function sampleBake(vol: BakeVolume, x: number, y: number, z: number, nx: number, ny: number, nz: number, offset: number, out: BakeSample): void {
  const u = (x + nx * offset - vol.origin[0]) / (vol.dims[0] * vol.cell);
  const v = (y + ny * offset - vol.origin[1]) / (vol.dims[1] * vol.cell);
  const w = (z + nz * offset - vol.origin[2]) / (vol.dims[2] * vol.cell);
  fetchLinear(vol.occ, vol.dims, u, v, w, texel);
  out.ao = clamp01(texel[0] + (texel[1] - 0.5) * nx + (texel[2] - 0.5) * ny + (texel[3] - 0.5) * nz);
  out.openness = texel[0];
  fetchLinear(vol.sky, vol.coarseDims, u, v, w, texel);
  out.sky = clamp01(texel[0] + SKY_B_RANGE * ((texel[1] - 0.5) * nx + (texel[2] - 0.5) * ny + (texel[3] - 0.5) * nz));
  fetchLinear(vol.bounce, vol.coarseDims, u, v, w, texel);
  const bounceY = Math.max(texel[0] + (texel[1] - 0.5) * nx + (texel[2] - 0.5) * ny + (texel[3] - 0.5) * nz, 0) * vol.bounceScale;
  fetchLinear(vol.tint, vol.coarseDims, u, v, w, texel);
  chromaColor(texel[0] * CHROMA_MAX, texel[1] * CHROMA_MAX, chroma);
  for (let c = 0; c < 3; c++) out.bounce[c] = bounceY * chroma[c]!;
  const lampY = texel[2] * texel[2] * vol.lampScale;
  const [a, b] = vol.lampTints;
  for (let c = 0; c < 3; c++) out.lamp[c] = lampY * (a[c]! + (b[c]! - a[c]!) * texel[3]);
}
